// src/components/AddCategoriaProductoForm.jsx
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import "animate.css";
import "../css/addlocalidadform.css";

import {
  postCategoriaProducto as addCategoriaProducto,
  getCategoriasProductos,
} from "../helpers/rutaCategoriasProductos";

/* ---------------- utils ---------------- */
const clean = (v) => String(v ?? "").trim();

const normalizarCodigo = (v) =>
  clean(v)
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/[^A-Z0-9_]/g, "");

const toIntOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeCategorias = (r) => {
  const arr = Array.isArray(r?.categorias)
    ? r.categorias
    : Array.isArray(r)
    ? r
    : [];

  return arr.map((c) => ({
    ...c,
    codigo: clean(c?.codigo).toUpperCase(),
    nombre: clean(c?.nombre).toUpperCase(),
  }));
};

/* ---------------- initial ---------------- */
const initial = {
  codigo: "",
  nombre: "",
  descripcion: "",
  grupoRecepcion: "OTRO",
  limitePorReceptorPorVenta: "0",
  usaColorDefault: false,
  activo: true,
};

export default function AddCategoriaProductoForm({ setShow }) {
  const [formValues, setFormValues] = useState(initial);
  const [showSuccess, setShowSuccess] = useState(false);
  const [codigoManual, setCodigoManual] = useState(false);

  const [categoriasState, setCategoriasState] = useState({
    data: [],
    loading: true,
  });

  const setField = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  /* =========================================================
     CARGA INICIAL
  ========================================================= */
  useEffect(() => {
    getCategoriasProductos({ limit: 200 })
      .then((r) => {
        const data = normalizeCategorias(r);

        setCategoriasState({
          data,
          loading: false,
        });
      })
      .catch((error) => {
        console.error("❌ Error cargando categorías:", error?.response?.data || error);

        setCategoriasState({
          data: [],
          loading: false,
        });
      });
  }, []);

  /* =========================================================
     CAMBIOS
  ========================================================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "nombre") {
      const nombre = value.toUpperCase();

      setFormValues((prev) => ({
        ...prev,
        nombre,
        codigo: codigoManual ? prev.codigo : normalizarCodigo(nombre),
      }));

      return;
    }

    if (name === "codigo") {
      setCodigoManual(true);
      setField(name, normalizarCodigo(value));
      return;
    }

    if (name === "usaColorDefault") {
      setField(name, checked);
      return;
    }

    if (type === "checkbox") {
      setField(name, checked);
      return;
    }

    if (name === "grupoRecepcion") {
      const grupo = clean(value).toUpperCase();

      setFormValues((prev) => {
        let limite = prev.limitePorReceptorPorVenta;
        let usaColor = prev.usaColorDefault;

        if (grupo === "VEHICULO") {
          limite = "1";
          usaColor = true;
        }

        if (grupo === "ENERGIA") {
          limite = "0";
          usaColor = false;
        }

        return {
          ...prev,
          grupoRecepcion: grupo,
          limitePorReceptorPorVenta: limite,
          usaColorDefault: usaColor,
        };
      });

      return;
    }

    setField(name, value);
  };

  /* =========================================================
     VALIDACIÓN / PAYLOAD
  ========================================================= */
  const validate = () => {
    const codigo = normalizarCodigo(formValues.codigo);
    const nombre = clean(formValues.nombre).toUpperCase();
    const limite = toIntOrZero(formValues.limitePorReceptorPorVenta);

    if (!codigo) return "Ingresá el código de la categoría.";
    if (!nombre) return "Ingresá el nombre de la categoría.";

    if (limite < 0) {
      return "El límite por receptor no puede ser negativo.";
    }

    const existeCodigo = categoriasState.data.some(
      (c) => clean(c.codigo).toUpperCase() === codigo
    );

    if (existeCodigo) {
      return `Ya existe una categoría con el código ${codigo}.`;
    }

    const existeNombre = categoriasState.data.some(
      (c) => clean(c.nombre).toUpperCase() === nombre
    );

    if (existeNombre) {
      return `Ya existe una categoría con el nombre ${nombre}.`;
    }

    return "";
  };

  const buildPayload = () => {
    return {
      codigo: normalizarCodigo(formValues.codigo),
      nombre: clean(formValues.nombre).toUpperCase(),
      descripcion: clean(formValues.descripcion).toUpperCase(),
      grupoRecepcion: clean(formValues.grupoRecepcion || "OTRO").toUpperCase(),
      limitePorReceptorPorVenta: toIntOrZero(
        formValues.limitePorReceptorPorVenta
      ),
      usaColorDefault: Boolean(formValues.usaColorDefault),
      activo: true,
    };
  };

  const resetForm = useCallback(() => {
    setFormValues(initial);
    setCodigoManual(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();
    if (msg) return alert(msg);

    const payload = buildPayload();

    try {
      const resp = await addCategoriaProducto(payload);
      const nuevaCategoria = resp?.categoria || payload;

      setCategoriasState((prev) => ({
        ...prev,
        data: [
          ...prev.data,
          {
            ...nuevaCategoria,
            codigo: payload.codigo,
            nombre: payload.nombre,
          },
        ],
      }));

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        resetForm();

        if (typeof setShow === "function") {
          setShow(false);
        }
      }, 900);
    } catch (error) {
      console.error(
        "❌ Error al crear categoría:",
        error?.response?.data || error
      );

      alert("No se pudo guardar la categoría. Revisá consola / Network.");
    }
  };

  return (
    <>
      {!categoriasState.loading && (
        <div className="container">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group mt-3 col-sm-3">
                <label>Código</label>
                <input
                  type="text"
                  className="form-control"
                  name="codigo"
                  maxLength="40"
                  value={formValues.codigo}
                  onChange={handleChange}
                  placeholder="Ej: MOTO"
                />
                <small className="text-muted">
                  Se genera desde el nombre, pero se puede editar.
                </small>
              </div>

              <div className="form-group mt-3 col-sm-5">
                <label>Nombre</label>
                <input
                  type="text"
                  className="form-control"
                  name="nombre"
                  maxLength="80"
                  value={formValues.nombre}
                  onChange={handleChange}
                  placeholder="Ej: MOTO"
                />
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Grupo recepción</label>
                <select
                  className="form-control"
                  name="grupoRecepcion"
                  value={formValues.grupoRecepcion}
                  onChange={handleChange}
                >
                  <option value="VEHICULO">VEHÍCULO</option>
                  <option value="ENERGIA">ENERGÍA</option>
                  <option value="ACCESORIO">ACCESORIO</option>
                  <option value="REPUESTO">REPUESTO</option>
                  <option value="OTRO">OTRO</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-8">
                <label>Descripción</label>
                <input
                  type="text"
                  className="form-control"
                  name="descripcion"
                  maxLength="150"
                  value={formValues.descripcion}
                  onChange={handleChange}
                  placeholder="Descripción opcional"
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Límite receptor</label>
                <input
                  type="number"
                  className="form-control"
                  name="limitePorReceptorPorVenta"
                  value={formValues.limitePorReceptorPorVenta}
                  onChange={handleChange}
                  min="0"
                />
                <small className="text-muted">
                  0 = sin límite
                </small>
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Usa color</label>

                <div
                  className="form-control d-flex align-items-center"
                  style={{ height: 38 }}
                >
                  <Form.Check
                    type="checkbox"
                    name="usaColorDefault"
                    label="Sí"
                    checked={Boolean(formValues.usaColorDefault)}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="alert alert-secondary mt-4 mb-0">
              <b>Regla rápida:</b> para MOTO o TRICICLO usá grupo{" "}
              <b>VEHÍCULO</b>, límite <b>1</b> y color activado. Para KIT,
              BATERÍA, INVERSOR o PANEL usá grupo <b>ENERGÍA</b> y límite{" "}
              <b>0</b>.
            </div>

            <button type="submit" className="btn btn-dark mt-5 mb-3" id="button">
              Guardar
            </button>
          </form>
        </div>
      )}

      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Categoría generada correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}